import React, { ReactNode } from 'react';
import { BonusCardState } from 'src/components/CustomersBar/CustomerTab/PromotionHistory/BonusCardState';
import { BonusBalanceNavigationState } from 'src/components/CustomersBar/CustomerTab/PromotionHistory/PromotionHistoryTabs/BonusBalanceTab/BonusBalanceNavigation.state';
import { RewardsNavigationState } from 'src/components/CustomersBar/CustomerTab/PromotionHistory/PromotionHistoryTabs/RewardTab/RewardsNavigation.state';
import { LoadingV2 } from 'src/components/Loading/LoadingV2';
import { BonusModel as BonusRewardModel } from 'src/domains/casino/state/AppCasino/store/campaign-store/BonusModel';
import { BonusWinningsModel } from 'src/domains/casino/state/AppCasino/store/campaign-store/BonusWinningsModel';
import { Button } from 'src/domains/common/components/button/button';
import { ButtonPropsType } from 'src/domains/common/components/button/button.types';
import { observer } from 'src/utils/mobx-react';

export const MAX_WIDTH = 1617;

export type BonusModelType = BonusRewardModel | BonusWinningsModel;

export type ViewDetailsHandlerType = (bonusModel: BonusModelType) => void;

export const isBonusWinningsModel = (model: BonusModelType): model is BonusWinningsModel => {
    return 'balance' in model;
};

export const isBonusRewardModel = (model: BonusModelType): model is BonusRewardModel => {
    return 'totalAmmount' in model;
};

export const createBonusBalanceHandler = (state: BonusBalanceNavigationState): ViewDetailsHandlerType => {
    return (model) => {
        if (isBonusWinningsModel(model)) {
            if (state.activeModel === model) {
                state.setActiveModel(null);
            } else {
                state.setActiveModel(model);
            }
        }
    };
};

export const createRewardsHandler = (state: RewardsNavigationState): ViewDetailsHandlerType => {
    return (model) => {
        if (isBonusRewardModel(model)) {
            if (state.activeModel === model) {
                state.setActiveModel(null);
            } else {
                state.setActiveModel(model);
            }
        }
    };
};

const ButtonComponent = observer(
    'ButtonComponent',
    ({ children, variant = 'outline', ...restProps }: ButtonPropsType) => {
        return (
            <Button
                className={`[@media(min-width:${MAX_WIDTH + 1}px)]:flex-1 [@media(max-width:${MAX_WIDTH}px)]:w-full disabled:cursor-not-allowed`}
                variant={variant}
                {...restProps}
            >
                {children}
            </Button>
        );
    }
);

export const renderMoreInfoButton = (
    bonusModel: BonusModelType,
    state: BonusBalanceNavigationState | RewardsNavigationState
): ReactNode => {
    const campaignModel = bonusModel.campaignModel;

    if (campaignModel === 'loading') {
        return (
            <LoadingV2
                dataTest=''
                title='Loading campaign details...'
            />
        );
    }

    if (campaignModel?.status === 'error' || campaignModel === null) {
        return null;
    }

    const isBonusBalanceNavigationState = (
        state: BonusBalanceNavigationState | RewardsNavigationState
    ): state is BonusBalanceNavigationState => {
        return state instanceof BonusBalanceNavigationState;
    };

    const onViewDetailsClick = isBonusBalanceNavigationState(state)
        ? createBonusBalanceHandler(state)
        : createRewardsHandler(state);

    return <ButtonComponent onClick={(): void => onViewDetailsClick(bonusModel)}>More Info</ButtonComponent>;
};

export const BonusCardButtons = observer(
    'BonusCardButtons',
    ({
        model,
        accountId,
        bonusCardState,
        state,
    }: {
        model: BonusModelType;
        accountId: number;
        bonusCardState: BonusCardState;
        state: BonusBalanceNavigationState | RewardsNavigationState;
    }) => {
        const { bonusStatus, isLoading, handleCancelBonus } = bonusCardState;

        return (
            <div className={`flex flex-row-reverse gap-2 [@media(max-width:${MAX_WIDTH}px)]:flex-wrap`}>
                {bonusStatus === 'active' ? (
                    <ButtonComponent
                        variant='destructive'
                        isLoading={isLoading}
                        onClick={() => handleCancelBonus(accountId)}
                    >
                        Cancel
                    </ButtonComponent>
                ) : null}
                {renderMoreInfoButton(model, state)}
            </div>
        );
    }
);
